import { useCallback, useState, useMemo } from "react"
import { BaseFilters, SortConfig, UsePaginatedQueryReturn } from "../types/pagination"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "./useGraphQLClient"
import { DEFAULT_PAGE_SIZE } from "@hydrologiq/shared/config"
export interface AssetFilters extends BaseFilters {
  name?: string
  category?: string
  class?: string
  kind?: string
  status?: string
  reserved_site?: string
  location?: string
  monitoring_status?: string
  grouping?: string
  // Additional filters for consistency with legacy API
  current_order?: string
  order_status?: string
  order_name?: string
  customer?: string
  site?: string
  // New deployment filters - filter by where assets are actually deployed (via fulfillment)
  deployed_customer?: string
  deployed_site?: string
  has_h2_level?: string
  base?: string
}

export interface AssetFilterOptions {
  categories?: string[]
  classes?: string[]
  kinds?: gqlTypes.AssetKind[]
  statuses?: gqlTypes.AssetStatus[]
  locations?: gqlTypes.Location[]
  monitoringStatuses?: gqlTypes.AssetMonitoringStatus[]
  groupings?: string[]
  orderStatuses?: gqlTypes.OrderStatus[]
  customers?: gqlTypes.Customer[]
  sites?: gqlTypes.Site[]
  bases?: gqlTypes.Site[]
}

/**
 * Hook for paginated assets deployed to a specific customer site
 * Uses the getAssets query with deployedCustomer/deployedSite filters
 */
export function usePaginatedSiteAssets(
  customerId?: string,
  siteId?: string,
  initialPageSize: number = DEFAULT_PAGE_SIZE
): UsePaginatedQueryReturn<gqlTypes.Asset, AssetFilters> {
  // Local state for pagination and filtering
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [filters, setFiltersState] = useState<AssetFilters>({})
  const [sort, setSortState] = useState<SortConfig | null>({ field: "created_at", direction: "desc" })

  // Build GraphQL variables
  const variables = useMemo(() => {
    const vars: any = {
      page: currentPage,
      perPage: pageSize,
      // Pre-set deployment filters for this customer site
      deployedCustomer: customerId,
      deployedSite: siteId,
    }

    // Add additional filter variables (only include non-empty values)
    if (filters.name) vars.name = filters.name
    if (filters.category) vars.category = filters.category
    if (filters.class) vars.class = filters.class
    if (filters.kind) vars.kind = filters.kind
    if (filters.status) vars.status = filters.status
    if (filters.reserved_site) vars.reservedSite = filters.reserved_site
    if (filters.location) vars.location = filters.location
    if (filters.monitoring_status) vars.monitoringStatus = filters.monitoring_status
    if (filters.grouping) vars.grouping = filters.grouping
    if (filters.current_order) vars.currentOrder = filters.current_order
    if (filters.order_status) vars.orderStatus = filters.order_status
    if (filters.order_name) vars.orderName = filters.order_name
    if (filters.customer) vars.customer = filters.customer
    if (filters.site) vars.site = filters.site
    if (filters.has_h2_level) vars.hasH2Level = filters.has_h2_level
    if (filters.base) vars.base = filters.base

    // Add sort variable
    if (sort?.field && sort?.direction) {
      vars.sort = `${sort.field}:${sort.direction}`
    }

    return vars
  }, [currentPage, pageSize, filters, sort, customerId, siteId])

  // Use primary endpoint (experience API) - this will use the enhanced getAssets query
  const { data, loading, error, refetch } = useQueryWithEndpoint(
    gqlTypes.GetAssetsDocument,
    {
      variables,
      fetchPolicy: "network-only",
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    },
    "primary" // Use the primary (experience API) endpoint
  )

  // Extract data from GraphQL response
  const assets = data?.assets?.data || []
  const pagination = data?.assets?.meta || null
  const filterOptions = data?.assets?.filterOptions || null

  // Main fetch function
  const fetchPage = useCallback(
    async (page?: number, newPageSize?: number, newFilters?: AssetFilters, newSort?: SortConfig | null) => {
      if (page !== undefined) setCurrentPage(page)
      if (newPageSize !== undefined) setPageSize(newPageSize)
      if (newFilters !== undefined) setFiltersState(newFilters)
      if (newSort !== undefined) setSortState(newSort)
    },
    []
  )

  // Filter and sort setters
  const setFilters = useCallback((newFilters: AssetFilters) => {
    setFiltersState(newFilters)
    setCurrentPage(1) // Reset to first page when filters change
  }, [])

  const setSort = useCallback((newSort: SortConfig | null) => {
    setSortState(newSort)
    setCurrentPage(1) // Reset to first page when sort changes
  }, [])

  const clearFilters = useCallback(() => {
    setFiltersState({})
    setCurrentPage(1)
  }, [])

  const refetchWrapper = useCallback(async () => {
    await refetch()
  }, [refetch])

  return {
    data: assets,
    pagination,
    filterOptions,
    loading: loading || false,
    error: error?.message || null,
    filters,
    sort,
    fetchPage,
    setFilters,
    setSort,
    clearFilters,
    refetch: refetchWrapper,
  }
}
